import {ReactElement, useRef} from 'react';
import {Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from '@mui/material';
import {useTranslation} from 'react-i18next';
import useConfirmDialogContext from 'contexts/ConfirmDialogContext/useConfirmDialogContext';

type Option = {
  id: number;
  name: string;
};

type Props = {
  id: string;
  label: string;
  placeholder: string;
  value: number | null;
  options: Option[];
  onChange: (value: number) => void;
  hasSelectedTray: boolean;
  deleteTrayFromSection: () => void;
};

export default function SectionSelect({
  id,
  label,
  placeholder,
  value,
  options,
  onChange,
  hasSelectedTray,
  deleteTrayFromSection
}: Props): ReactElement {
  const {t} = useTranslation('common');
  const shouldShowConfirmDialog = useRef(hasSelectedTray);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openConfirmDialog = useConfirmDialogContext();

  function handleChange(event: SelectChangeEvent) {
    const newValue = parseInt(event.target.value);
    if (shouldShowConfirmDialog.current) {
      openConfirmDialog({
        onConfirm: function () {
          onChange(newValue);
          shouldShowConfirmDialog.current = false;
          setTimeout(() => inputRef.current?.focus(), 0);
          deleteTrayFromSection();
        },
        title: t('common:confirmResetTray.title'),
        text: t('common:confirmResetTray.text')
      });
    } else {
      onChange(newValue);
    }
  }

  const labelId = `${id}-label`;

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        id={id}
        label={label}
        displayEmpty
        notched
        value={value == null ? '' : `${value}`}
        onChange={handleChange}
        inputRef={inputRef}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Box component="span" sx={{color: 'text.secondary'}}>
                {placeholder}
              </Box>
            );
          }
          return options.find((option) => `${option.id}` === selected)?.name;
        }}
      >
        {options.map((option) => (
          <MenuItem value={`${option.id}`} key={`${id}--${option.id}`}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
